import { supabase } from '@/config/supabase'
import { UserLimits } from '@/types'

export interface AuthUser {
  id: string
  email?: string
  username?: string
  app_metadata?: {
    provider?: string
    provider_id?: string
    [key: string]: any
  }
  user_metadata?: {
    sub?: string
    [key: string]: any
  }
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterCredentials {
  email: string
  password: string
  username: string
}

class AuthService {
  async signIn({ email, password }: LoginCredentials): Promise<AuthUser> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      throw new Error(error.message)
    }

    if (!data.user) {
      throw new Error('Giriş başarısız')
    }

    return {
      id: data.user.id,
      email: data.user.email,
      username: data.user.user_metadata?.username,
      app_metadata: data.user.app_metadata,
      user_metadata: data.user.user_metadata,
    }
  }

  async signUp({ email, password, username }: RegisterCredentials): Promise<AuthUser> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: username,
        },
      },
    })

    if (error) {
      throw new Error(error.message)
    }

    if (!data.user) {
      throw new Error('Kayıt başarısız')
    }

    return {
      id: data.user.id,
      email: data.user.email,
      username: data.user.user_metadata?.username,
      app_metadata: data.user.app_metadata,
      user_metadata: data.user.user_metadata,
    }
  }

  async ensureAuthenticated(): Promise<AuthUser> {
    const { data: { user }, error } = await supabase.auth.getUser()
    
    if (error || !user) {
      throw new Error('Oturum bulunamadı')
    }
    
    return {
      id: user.id,
      email: user.email,
      username: user.user_metadata?.username,
      app_metadata: user.app_metadata,
      user_metadata: user.user_metadata,
    }
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) return null
    
    return {
      id: user.id,
      email: user.email,
      username: user.user_metadata?.username,
      app_metadata: user.app_metadata,
      user_metadata: user.user_metadata,
    }
  }

  async signOut(): Promise<void> {
    await supabase.auth.signOut()
  }

  async signInWithGoogle(redirectPath?: string): Promise<void> {
    // Native: use GoogleAuth plugin to get idToken and sign into Supabase directly
    const { Capacitor } = await import('@capacitor/core')
    if (Capacitor.isNativePlatform()) {
      try {
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth')
        const platform = Capacitor.getPlatform()
        const iosClientId = (import.meta as any).env?.VITE_GOOGLE_IOS_CLIENT_ID
        // Android'de idToken için Web Client ID kullanılmalı
        const webClientId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID
        const clientId = platform === 'ios' ? iosClientId : webClientId
        if (!clientId) throw new Error('Google Client ID eksik: ' + platform)
        await GoogleAuth.initialize({ clientId, scopes: ['email', 'profile', 'openid'] })
        const result: any = await GoogleAuth.signIn()
        const idToken = result?.authentication?.idToken || result?.idToken
        if (!idToken) throw new Error('Google idToken alınamadı')
        const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken })
        if (error) throw new Error(error.message)
        return
      } catch (err: any) {
        // Native akışta hata varsa web'e düşmeyelim; hatayı gösterelim
        console.error('Native Google Sign-In hatası:', err)
        const errorMessage = err?.message || err?.toString() || 'Native Google ile giriş başarısız'
        
        // Android'de SHA-1 hatası için özel mesaj
        if (errorMessage.includes('10:') || errorMessage.includes('DEVELOPER_ERROR') || errorMessage.includes('sign in')) {
          throw new Error('Google Sign-In yapılandırma hatası. Lütfen SHA-1 fingerprint\'in Google Cloud Console\'da tanımlı olduğundan emin olun.')
        }
        
        throw new Error(errorMessage)
      }
    }

    // Web/SPAs: OAuth redirect flow
    const redirectTo = redirectPath
      ? `${window.location.origin}${redirectPath}`
      : (import.meta as any).env?.VITE_OAUTH_REDIRECT_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    })
    if (error) {
      throw new Error(error.message)
    }
  }


  async signInWithApple(): Promise<void> {
    try {
      // Check if we're on mobile (Capacitor)
      const { Capacitor } = await import('@capacitor/core')
      
      if (Capacitor.isNativePlatform()) {
        // Use native Apple Sign-In on iOS
        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')
        
        const result = await SignInWithApple.authorize()
        
        if (result.response && result.response.identityToken) {
          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: result.response.identityToken
          })
          
          if (error) {
            throw new Error(error.message)
          }
        } else {
          throw new Error('Apple Sign-In iptal edildi')
        }
      } else {
        // Use web Apple Sign-In
        const redirectTo = (import.meta as any).env?.VITE_OAUTH_REDIRECT_URL || window.location.origin
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo,
          },
        })
        
        if (error) {
          throw new Error(error.message)
        }
      }
    } catch (error) {
      console.error('Apple Sign-In error:', error)
      throw new Error(error instanceof Error ? error.message : 'Apple ile giriş yapılırken hata oluştu')
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    if (!email || !email.includes('@')) {
      throw new Error('Geçerli bir e-posta adresi giriniz')
    }
    const redirectUrl = `${window.location.origin}/auth/reset-password`
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    })
    if (error) {
      // Güvenlik açısından dışarıya spesifik hata vermeyelim
      throw new Error('Eğer bu e-posta sistemde kayıtlı ise, sıfırlama bağlantısı gönderilecektir')
    }
  }

  async deleteAccount(): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Oturum bulunamadı')

    const { user } = session
    
    try {
      // Mark account as deleted in our security tracking system
      await supabase.rpc('mark_account_deleted', {
        p_user_id: user.id
      })
    } catch (error) {
      console.error('Error marking account as deleted:', error)
      // Continue with deletion even if security tracking fails
    }

    // Edge Function: delete-user (admin key ile çalışır)
    const baseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
    const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY
    if (!baseUrl || !anon) throw new Error('Supabase yapılandırması eksik')

    const resp = await fetch(`${baseUrl}/functions/v1/delete-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': anon,
      },
      body: JSON.stringify({ user_id: user.id }),
    })

    if (!resp.ok) {
      const text = await resp.text().catch(() => '')
      throw new Error(text || 'Hesap silme başarısız')
    }

    await this.signOut()
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        callback({
          id: session.user.id,
          email: session.user.email,
          username: session.user.user_metadata?.username,
          app_metadata: session.user.app_metadata,
          user_metadata: session.user.user_metadata,
        })
      } else {
        callback(null)
      }
    })
  }

  async getUserLimits(userId?: string): Promise<UserLimits | null> {
    const user = await this.getCurrentUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) return null

    try {
      const { data, error } = await supabase.rpc('get_user_limits', {
        p_user_id: targetUserId
      })

      if (error) {
        console.error('Error getting user limits:', error)
        return null
      }

      return {
        id: data.id,
        userId: data.user_id,
        promptCount: data.prompt_count,
        maxPrompts: data.max_prompts,
        resetDate: data.reset_date,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      }
    } catch (error) {
      console.error('Error getting user limits:', error)
      return null
    }
  }

  async initializeUserLimitsSecure(provider: string, providerData?: { providerId?: string, email?: string }): Promise<UserLimits | null> {
    const user = await this.getCurrentUser()
    if (!user?.id) return null

    try {
      // Eski çoklu imza (text vs varchar) çakışmalarını önlemek için AUTO sürümünü çağır
      // Parametreler gerekmiyor; sağlayıcı bilgisi ve email DB içinde tespit edilir
      void provider; void providerData;
      const { data, error } = await supabase.rpc('initialize_user_limits_secure_auto', {
        p_user_id: user.id,
      })

      if (error) {
        throw new Error(error.message)
      }

      return {
        id: data.id,
        userId: data.user_id,
        promptCount: data.prompt_count,
        maxPrompts: data.max_prompts,
        resetDate: data.reset_date,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      }
    } catch (error) {
      console.error('Error initializing secure user limits:', error)
      throw error
    }
  }

  async canUserCreatePrompt(userId?: string): Promise<boolean> {
    const user = await this.getCurrentUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) return false

    try {
      const { data, error } = await supabase.rpc('can_user_create_prompt', {
        p_user_id: targetUserId
      })

      if (error) {
        console.error('Error checking user prompt limit:', error)
        return false
      }

      return data === true
    } catch (error) {
      console.error('Error checking user prompt limit:', error)
      return false
    }
  }

  async incrementUserPromptCount(userId?: string): Promise<UserLimits | null> {
    const user = await this.getCurrentUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) return null

    try {
      const { data, error } = await supabase.rpc('increment_user_prompt_count', {
        p_user_id: targetUserId
      })

      if (error) {
        throw new Error(error.message)
      }

      return {
        id: data.id,
        userId: data.user_id,
        promptCount: data.prompt_count,
        maxPrompts: data.max_prompts,
        resetDate: data.reset_date,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      }
    } catch (error) {
      console.error('Error incrementing user prompt count:', error)
      throw error
    }
  }

  async updateUsername(newUsername: string): Promise<AuthUser> {
    if (!newUsername || newUsername.trim().length < 3) {
      throw new Error('Kullanıcı adı en az 3 karakter olmalıdır')
    }

    if (newUsername.trim().length > 30) {
      throw new Error('Kullanıcı adı en fazla 30 karakter olabilir')
    }

    const trimmedUsername = newUsername.trim()

    // Kullanıcı adı validasyonu (sadece harf, rakam, alt çizgi)
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedUsername)) {
      throw new Error('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir')
    }

    const currentUser = await this.getCurrentUser()
    if (!currentUser) {
      throw new Error('Oturum bulunamadı')
    }

    try {
      const { data, error } = await supabase.auth.updateUser({
        data: {
          username: trimmedUsername,
        },
      })

      if (error) {
        throw new Error(error.message)
      }

      if (!data.user) {
        throw new Error('Kullanıcı bilgileri güncellenemedi')
      }

      return {
        id: data.user.id,
        email: data.user.email,
        username: data.user.user_metadata?.username,
        app_metadata: data.user.app_metadata,
        user_metadata: data.user.user_metadata,
      }
    } catch (error) {
      console.error('Error updating username:', error)
      throw error
    }
  }
}

export const authService = new AuthService()
export default authService
